import { useMemo, useState, useEffect } from 'react';
import { ITEMS_PER_PAGE } from './constants';
import { IFiling, IFilters } from './types';

export const useGetFiltersOptions = (filingsArr: IFiling[]) => {
  const filtersOptions = useMemo(() => {
    const years = Array.from(new Set(filingsArr.map(filing => filing.year)));
    const yearsOptions = years.map(year => ({ value: year, label: year }));

    const filingTypes = Array.from(
      new Set(filingsArr.map(filing => filing.form)),
    ).sort();
    const filingTypesOptions = filingTypes.map(filingType => ({
      value: filingType,
      label: filingType,
    }));

    return { yearsOptions, filingTypesOptions };
  }, [filingsArr]);

  return filtersOptions;
};

export const useGetFilteredFilingsArrAndPageCount = (
  filingsArr: IFiling[],
  activeFilters: IFilters,
) => {
  const result = useMemo(() => {
    const { year, filingType } = activeFilters;
    let resultArr = [...filingsArr];
    if (year || filingType) {
      resultArr = filingsArr.filter(filing => {
        return (
          (year ? year === filing.year : true) &&
          (filingType ? filingType === filing.form : true)
        );
      });
    }
    return {
      filteredFilingsArr: resultArr,
      pagesCount: Math.ceil(resultArr.length / ITEMS_PER_PAGE),
    };
  }, [filingsArr, activeFilters]);
  return result;
};

interface Size {
  width: number | undefined;
  height: number | undefined;
}

export const useWindowSize = (): Size => {
  const [windowSize, setWindowSize] = useState<Size>({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
};

export const useOnClickOutside = (ref: any, handler: any) => {
  useEffect(() => {
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};
